import { Routes, Route, Navigate, NavLink } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth, getRedirectAuthResult } from '@/lib/firebase';
import {
  dispatchAuthResultToExtension,
  getGoogleCredentialFromResult,
  EXTENSION_TAB_MODE_KEY,
} from '@/lib/auth-extension';
import Layout from '@/components/Layout';
import AuthPage from '@/routes/AuthPage';
import AuthCallback from '@/routes/AuthCallback';
import Billing from '@/routes/Billing';
import Feed from '@/routes/Feed';
import Privacy from '@/routes/Privacy';
import Terms from '@/routes/Terms';
import WebAppGenerator from '@/routes/WebAppGenerator';
import Admin from '@/routes/Admin';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center bg-mirror-bg text-mirror-muted text-lg">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  return <>{children}</>;
}

export default function App() {
  const [handlingRedirect, setHandlingRedirect] = useState(true);
  const [extensionAuthSuccess, setExtensionAuthSuccess] = useState(false);

  useEffect(() => {
    getRedirectAuthResult()
      .then((result) => {
        const isExtensionTab = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(EXTENSION_TAB_MODE_KEY) === 'tab';
        if (result?.user && isExtensionTab) {
          const cred = getGoogleCredentialFromResult(result as { credential?: { idToken?: string | null; accessToken?: string | null } | null });
          if (cred) {
            // Defer so extension content script has time to attach its listener
            setTimeout(() => {
              dispatchAuthResultToExtension(cred.idToken, cred.accessToken);
            }, 0);
            setExtensionAuthSuccess(true);
          }
          sessionStorage.removeItem(EXTENSION_TAB_MODE_KEY);
        }
      })
      .finally(() => setHandlingRedirect(false));
  }, []);

  if (handlingRedirect) {
    return <div className="flex min-h-screen items-center justify-center bg-mirror-bg text-mirror-muted text-lg">Completing sign-in…</div>;
  }

  if (extensionAuthSuccess) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6 bg-mirror-bg">
        <div className="w-full max-w-[360px] rounded-xl border border-mirror-border bg-mirror-card p-8 text-center">
          <h1 className="mb-2 text-xl font-semibold text-emerald-400">Sign-in complete!</h1>
          <p className="text-mirror-muted">
            Click the extension icon in your toolbar to continue.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* Public legal pages (required by Google Web Store — no auth) */}
      <Route
        path="/privacy"
        element={
          <div className="min-h-screen bg-mirror-bg text-mirror-text">
            <header className="border-b border-mirror-border bg-mirror-bg/95 backdrop-blur px-6 py-3">
              <NavLink to="/" className="text-mirror-gold no-underline hover:underline text-sm">
                ← Back to Mirror
              </NavLink>
            </header>
            <main className="mx-auto max-w-4xl px-6 py-8">
              <Privacy />
            </main>
          </div>
        }
      />
      <Route
        path="/terms"
        element={
          <div className="min-h-screen bg-mirror-bg text-mirror-text">
            <header className="border-b border-mirror-border bg-mirror-bg/95 backdrop-blur px-6 py-3">
              <NavLink to="/" className="text-mirror-gold no-underline hover:underline text-sm">
                ← Back to Mirror
              </NavLink>
            </header>
            <main className="mx-auto max-w-4xl px-6 py-8">
              <Terms />
            </main>
          </div>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="billing" element={<Billing />} />
        <Route index element={<WebAppGenerator />} />
        <Route path="feed" element={<Feed />} />
        {import.meta.env.DEV && <Route path="admin" element={<Admin />} />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
